import { Action } from 'redux';
import { ApiResponse } from 'apisauce';
import { ThunkAction } from 'redux-thunk';
import { SearchResponse } from '@robocorp/robocloud-search-iternal-sdk';
import { ErrorAction, OkAction } from './baseActions';
import { assistantRunService } from '../services/assistantAPI';
import { AssistantRun } from '../types/resources';
import { formatApiErrorPayload } from './utils';
import * as searchService from '~/services/searchAPI';
import { IndexedAssistantRunsQueryArgs } from '~/services/searchAPI';
import { selectCurrentWorkspaceId } from './utils/selectors';

// To avoid circular imports between modules, we must import only the AppState type here
type AppState = import('~/store').AppState;

export enum AssistantRunActionType {
  LIST_ASSISTANT_RUNS = 'LIST_ASSISTANT_RUNS',
  LIST_ASSISTANT_RUNS_OK = 'LIST_ASSISTANT_RUNS_OK',
  LIST_ASSISTANT_RUNS_ERROR = 'LIST_ASSISTANT_RUNS_ERROR',
  SILENT_GET_ASSISTANT_RUN = 'SILENT_GET_ASSISTANT_RUN',
  GET_ASSISTANT_RUN = 'GET_ASSISTANT_RUN',
  GET_ASSISTANT_RUN_OK = 'GET_ASSISTANT_RUN_OK',
  GET_ASSISTANT_RUN_ERROR = 'GET_ASSISTANT_RUN_ERROR',
  DELETE_ASSISTANT_RUN = 'DELETE_ASSISTANT_RUN',
  DELETE_ASSISTANT_RUN_OK = 'DELETE_ASSISTANT_RUN_OK',
  DELETE_ASSISTANT_RUN_ERROR = 'DELETE_ASSISTANT_RUN_ERROR',
  LIST_WORKSPACE_ASSISTANT_RUNS = 'LIST_WORKSPACE_ASSISTANT_RUNS',
  LIST_WORKSPACE_ASSISTANT_RUNS_OK = 'LIST_WORKSPACE_ASSISTANT_RUNS_OK',
  LIST_WORKSPACE_ASSISTANT_RUNS_ERROR = 'LIST_WORKSPACE_ASSISTANT_RUNS_ERROR',
}

type WithAssistantId = { assistantId: string };
type WithAssistantRunId = { assistantRunId: string };

type ListAssistantRunsAction =
  | Action<AssistantRunActionType.LIST_ASSISTANT_RUNS>
  | OkAction<AssistantRunActionType.LIST_ASSISTANT_RUNS_OK, SearchResponse<AssistantRun>>
  | ErrorAction<AssistantRunActionType.LIST_ASSISTANT_RUNS_ERROR>;

type GetAssistantRunAction =
  | Action<AssistantRunActionType.SILENT_GET_ASSISTANT_RUN>
  | (Action<AssistantRunActionType.GET_ASSISTANT_RUN> & WithAssistantId & WithAssistantRunId)
  | (OkAction<AssistantRunActionType.GET_ASSISTANT_RUN_OK, AssistantRun> &
      WithAssistantId &
      WithAssistantRunId)
  | (ErrorAction<AssistantRunActionType.GET_ASSISTANT_RUN_ERROR> &
      WithAssistantId &
      WithAssistantRunId);

type DeleteAssistantRunAction =
  | (Action<AssistantRunActionType.DELETE_ASSISTANT_RUN> & WithAssistantId & WithAssistantRunId)
  | (OkAction<AssistantRunActionType.DELETE_ASSISTANT_RUN_OK, AssistantRun> &
      WithAssistantId &
      WithAssistantRunId)
  | (ErrorAction<AssistantRunActionType.DELETE_ASSISTANT_RUN_ERROR> &
      WithAssistantId &
      WithAssistantRunId);

type ListWorkspaceAssistantRunsAction =
  | Action<AssistantRunActionType.LIST_WORKSPACE_ASSISTANT_RUNS>
  | OkAction<AssistantRunActionType.LIST_WORKSPACE_ASSISTANT_RUNS_OK, AssistantRun[]>
  | ErrorAction<AssistantRunActionType.LIST_WORKSPACE_ASSISTANT_RUNS_ERROR>;

export type AssistantRunAction =
  | ListAssistantRunsAction
  | GetAssistantRunAction
  | ListWorkspaceAssistantRunsAction
  | DeleteAssistantRunAction;

export const listAssistantRuns =
  (
    assistantId: string,
    queryArgs?: IndexedAssistantRunsQueryArgs,
  ): ThunkAction<void, AppState, null, ListAssistantRunsAction> =>
  async (dispatch, getState) => {
    dispatch({ type: AssistantRunActionType.LIST_ASSISTANT_RUNS });

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await searchService.getAssistantRuns({ workspaceId, assistantId }, queryArgs);
    if (response.ok) {
      dispatch({
        type: AssistantRunActionType.LIST_ASSISTANT_RUNS_OK,
        data: response.data as unknown as SearchResponse<AssistantRun>,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: AssistantRunActionType.LIST_ASSISTANT_RUNS_ERROR,
      });
    }
  };

export const deleteAssistantRun =
  (
    assistantId: string,
    assistantRunId: string,
  ): ThunkAction<Promise<ApiResponse<AssistantRun>>, AppState, null, DeleteAssistantRunAction> =>
  async (dispatch, getState) => {
    dispatch({
      type: AssistantRunActionType.DELETE_ASSISTANT_RUN,
      assistantRunId,
      assistantId,
    });

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await assistantRunService.deleteAssistantRun({
      workspaceId,
      assistantId,
      runId: assistantRunId,
    });
    if (response.ok) {
      dispatch({
        type: AssistantRunActionType.DELETE_ASSISTANT_RUN_OK,
        assistantId,
        assistantRunId,
        data: response.data as AssistantRun,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: AssistantRunActionType.DELETE_ASSISTANT_RUN_ERROR,
        assistantId,
        assistantRunId,
      });
    }

    return response;
  };

export const getAssistantRun =
  (
    assistantId: string,
    assistantRunId: string,
  ): ThunkAction<void, AppState, null, GetAssistantRunAction> =>
  async (dispatch, getState) => {
    if (getState().workspaceResources.assistantRuns.byId[assistantRunId]) {
      dispatch({ type: AssistantRunActionType.SILENT_GET_ASSISTANT_RUN });
    } else {
      dispatch({
        type: AssistantRunActionType.GET_ASSISTANT_RUN,
        assistantId,
        assistantRunId,
      });
    }

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await assistantRunService.getAssistantRun({
      workspaceId,
      assistantId,
      runId: assistantRunId,
    });
    if (response.ok) {
      dispatch({
        type: AssistantRunActionType.GET_ASSISTANT_RUN_OK,
        assistantId,
        assistantRunId,
        data: response.data as AssistantRun,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: AssistantRunActionType.GET_ASSISTANT_RUN_ERROR,
        assistantId,
        assistantRunId,
      });
    }
  };

export const listWorkspaceAssistantRuns =
  (): ThunkAction<void, AppState, null, ListWorkspaceAssistantRunsAction> =>
  async (dispatch, getState) => {
    dispatch({ type: AssistantRunActionType.LIST_WORKSPACE_ASSISTANT_RUNS });

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await assistantRunService.listWorkspaceAssistantRuns({ workspaceId });
    if (response.ok) {
      dispatch({
        type: AssistantRunActionType.LIST_WORKSPACE_ASSISTANT_RUNS_OK,
        data: response.data as AssistantRun[],
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: AssistantRunActionType.LIST_WORKSPACE_ASSISTANT_RUNS_ERROR,
      });
    }
  };
